import { Button, CircularProgress } from '@mui/material';
import type { AxiosError } from 'axios';
import { Sparkles, Undo2 } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { improveVideoPrompt, type PromptAssistInput } from '../api/promptAssist.api';
import type { GenerateVideoApiError } from '../types';

interface PromptAssistButtonProps {
  context: Omit<PromptAssistInput, 'prompt'>;
  prompt: string;
  onChange: (value: string) => void;
}

/** Rewrites the short idea into a detailed prompt; the original can be restored. */
export const PromptAssistButton: React.FC<PromptAssistButtonProps> = ({
  context,
  prompt,
  onChange,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [original, setOriginal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const improve = async () => {
    setLoading(true);
    setError(null);
    try {
      const improved = await improveVideoPrompt({ ...context, prompt: prompt.trim() });
      setOriginal(prompt);
      onChange(improved);
    } catch (caught) {
      const data = (caught as AxiosError<GenerateVideoApiError>).response?.data;
      setError(
        data?.errorCode === 'TOKEN_QUOTA_EXCEEDED'
          ? t('services.generateVideo.promptAssist.noTokens')
          : data?.errorCode === 'VIDEO_PROMPT_ASSIST_NOT_CONFIGURED'
            ? t('services.generateVideo.promptAssist.notConfigured')
            : t('services.generateVideo.promptAssist.failed')
      );
    } finally {
      setLoading(false);
    }
  };

  const undo = () => {
    if (original !== null) onChange(original);
    setOriginal(null);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="small"
        variant="outlined"
        disabled={loading || prompt.trim().length === 0}
        onClick={() => void improve()}
        startIcon={loading ? <CircularProgress size={14} /> : <Sparkles size={16} />}
      >
        {t('services.generateVideo.promptAssist.improve')}
      </Button>
      {original !== null && !loading && (
        <Button size="small" onClick={undo} startIcon={<Undo2 size={16} />}>
          {t('services.generateVideo.promptAssist.undo')}
        </Button>
      )}
      {error && <span className="text-sm text-red-600 dark:text-red-400">{error}</span>}
    </div>
  );
};
